import * as React from 'react'
import * as styles from './settings.page.styl'
import { map } from 'lodash'
import cx from 'classnames'
import * as View from '@src/components/common/view.page'
import Tabs, { Tab } from '@src/components/common/material/tabs'
import { Setting, Settings, SettingsTabUrl } from './configurations'
import { useHistory, useRouteMatch } from 'react-router-dom'
import { getMatchParamByKey, generateUrl } from '@src/utils/router.helpers'
import type { User, Tenant } from '@src/redux/reducers/auth'
import Tile from '@src/components/common/tile'
import type {
  UpdateUserResp,
  UpdateNotificationInfoResp,
  UpdateNotificationInfoReq
} from '@src/endpoints/services/tenantMgr.service'
import {
  AlertSeverityEnum,
  NotificationInfo
} from '@src/endpoints/services/commonHdr.service'
import uuidv4 from 'uuid/v4'
import { Routes } from '@src/constants/routes'
import { useDispatch, useSelector } from 'react-redux'
import * as CommonSelectors from '@src/selectors'
import * as AuthActions from '@src/redux/actions/auth.actions'

export interface SettingsPageParams {
  id: SettingsTabUrl
}
interface OwnProps {}
export type Props = OwnProps

const settingsPage: React.FC<Props> = (props: Props) => {
  const history = useHistory<SettingsPageParams>()
  const match = useRouteMatch<SettingsPageParams>()
  const tenant = useSelector(CommonSelectors.getTenant)
  const user = useSelector(CommonSelectors.getUser)
  const lastUpdate = useSelector(CommonSelectors.getLastUpdate)
  const dispatch = useDispatch()

  function onClickTab(url: SettingsTabUrl) {
    const settingTab = getMatchParamByKey({ match }, 'id')
    if (history && url !== settingTab) {
      history.push(generateUrl(Routes?.pages?.settings?.path, { id: url }))
    }
  }

  function updateUser(user: User, callback: (data?: UpdateUserResp) => void) {
    dispatch(
      AuthActions.updateUserDetails(
        { req_hdr: { tid: uuidv4() }, user },
        callback
      )
    )
  }

  function updateNotifications(
    severity: AlertSeverityEnum,
    notificationSettings: NotificationInfo,
    callback: (data?: UpdateNotificationInfoResp) => void
  ) {
    const payload: UpdateNotificationInfoReq = {
      req_hdr: { tid: uuidv4() },
      severity,
      notification_info: notificationSettings
    }
    dispatch(AuthActions.updateNotificationSettings(payload, callback))
  }

  function getAlertsInfo(severity: AlertSeverityEnum): NotificationInfo {
    const tenantInfo = tenant as Tenant
    const alertInfo = tenantInfo?.alertSettings || []
    return alertInfo.find(elem => elem.severity === severity)
  }

  function renderTabs() {
    const settingTab: SettingsTabUrl = getMatchParamByKey({ match }, 'id')
    const tabsComponents =
      map(Settings, (tab, key) => (
        <Tab
          classNameLabel={styles.tabLabel}
          key={key}
          label={tab?.title}
          value={key}
        />
      )) || []

    return (
      <div className={styles.tabs}>
        <Tabs value={settingTab} onChange={onClickTab} variant="scrollable">
          {tabsComponents}
        </Tabs>
      </div>
    )
  }

  function renderContent() {
    const settingTab: SettingsTabUrl = getMatchParamByKey({ match }, 'id')
    const currentTabProps: Setting = Settings[settingTab] || ({} as Setting)
    const { severity, footerComponent } = currentTabProps
    return (
      <Tile className={styles.tabContent}>
        {currentTabProps && currentTabProps.component && (
          <currentTabProps.component
            tenant={tenant}
            user={user}
            history={history}
            notificationSettings={getAlertsInfo(severity)}
            severity={severity}
            updateUser={!severity && updateUser}
            updateNotifications={severity && updateNotifications}
            footerComponent={footerComponent}
          />
        )}
      </Tile>
    )
  }

  return (
    <View.ContentPage noMargin noPadding className={styles.content}>
      <View.HeaderPage lastUpdate={lastUpdate} className={cx(styles.header)}>
        Settings
      </View.HeaderPage>
      <div className={styles.settingsWrapper}>
        {user && tenant ? renderTabs() : null}
        {renderContent()}
      </div>
    </View.ContentPage>
  )
}
export default settingsPage
